import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import FullCalendar from '@fullcalendar/react';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import interactionPlugin from '@fullcalendar/interaction';
import Moment from 'moment';
import { extendMoment } from 'moment-range';
const moment = extendMoment(Moment);
import PropTypes from 'prop-types';
import SchedulerPopup from './popup';
import _ from 'lodash';
import deepIs from 'deep-is';
/* CSS imports */
// import '@fullcalendar/core/main.css';
// import '@fullcalendar/timeline/main.css';
// import '@fullcalendar/resource-timeline/main.css';
// import '@fullcalendar/list/main.css';
import { isBrowser, isMobileOnly } from 'mobile-device-detect';  
import {
    RoomStatusTypes,
    MaintenanceStatusTypes,
} from '../../components/shared/statustypes';
import {
    Popup,
    Dropdown,
    Grid,
    Icon,
    Confirm,
    Menu,
    Checkbox,
    Button,
} from 'semantic-ui-react';
import {
    SetRoomHousekeepingStatusLog,
    GetReservationTotals,
} from '../../api/services/pms';
import { RoomPlannedMaintenance } from '../../api/services/next';
import { DatePicker } from '../../components/shared/DatePicker';
import {
    onWarning,
    onSuccess,
    onError,
} from '../../components/shared/messages';
import { RestrictionIcons } from '../../assets/assets';
import SettingsPopup from './settingsPopup';
import LanguageContext, { useTextResources } from '../../language';
import { GetSingleReservation, RebuildReservation } from '../../components/shared/reservation';
import { getContrastYIQ } from '../../components/shared/utils';
import SelectProductPopup from '../../components/SelectProductPopup';
import EventsPopup from './eventsPopup';
import { CalendarDisplaySettingsPopup } from './displaySettings';
import { Actions } from '../../components/shared/constants';
import { Currencies } from '../../components/shared/currencies';
import formatCurrency from 'format-currency';
import { Calendar, Products } from '../../api/services/next';
import enLocale from '@fullcalendar/core/locales/en-gb';
import heLocale from '@fullcalendar/core/locales/he';
import { useLoader } from '../../hooks/useLoader';
import ReservationPolicyUpdateConfirmationPopup from '../../components/ReservationPolicyUpdateConfirmationPopup';
heLocale.dir = "ltr";

/**
 * Scheduler component for displaying and managing hotel room reservations and events
 * using a timeline calendar interface (FullCalendar with resource timeline).
 *
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.hotels - List of hotel objects
 * @param {Object} props.viewSettings - Calendar view settings
 * @param {Object} props.hotelSettings - Hotel-specific settings
 * @param {Function} props.setViewSettings - Callback to update view settings
 * @param {Function} props.setHotelSettings - Callback to update hotel settings
 * @param {Function} props.saveReservation - Function to save reservation changes
 * @param {Function} props.eventClick - Callback for event click
 * @param {Function} props.eventsFunction - Function to fetch main events
 * @param {Function} props.dayDataFunction - Function to fetch day data events
 * @param {Function} props.priceDataFunction - Function to fetch price data events
 * @param {Function} props.colDataFunction - Function to fetch column data events
 * @param {Function} props.resourcesFunction - Function to fetch resources (rooms)
 * @param {Function} props.rebuildVirtualEvents - Callback to rebuild virtual events
 * @param {Function} props.rebuildDayUseEvents - Callback to rebuild day-use events
 * @param {React.Component} props.HotelFilterCmp - Hotel filter component
 *
 * @returns {JSX.Element} Scheduler calendar with controls, popups, and event handling
 *
 * @example
 * <Scheduler
 *   hotels={hotels}
 *   viewSettings={viewSettings}
 *   hotelSettings={hotelSettings}
 *   setViewSettings={setViewSettings}
 *   setHotelSettings={setHotelSettings}
 *   saveReservation={saveReservation}
 *   eventClick={handleEventClick}
 *   eventsFunction={fetchEvents}
 *   dayDataFunction={fetchDayData}
 *   priceDataFunction={fetchPriceData}
 *   colDataFunction={fetchColData}
 *   resourcesFunction={fetchResources}
 *   rebuildVirtualEvents={rebuildVirtualEvents}
 *   rebuildDayUseEvents={rebuildDayUseEvents}
 *   HotelFilterCmp={HotelFilterComponent}
 * />
 */
const Scheduler = (props) => {
    const {
        hotels
    } = props;
    const calendarRef = useRef(null);
    const [showPopup, setShowPopup] = useState(false);
    const [showPopupEvents, setShowPopupEvents] = useState(false);
    const [popupContext, setPopupContext] = useState(null);
    const [eventspopupContext, setEventsPopupContext] = useState(null);
    const [displayTooltip, setDisplayTooltip] = useState(false);
    const [tooltipContext, setTooltipContext] = useState(null);
    const [tooltipContent, setTooltipContent] = useState(null);
    const [tooltipHeader, setTooltipHeader] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState('left center');
    const [popupReservaion, setPopupReservaion] = useState(null);
    const [popupEvent, setPopupEvent] = useState(null);
    const [title, setTitle] = useState('');
    const [defaultView, setDefaultView] = useState(
        props.viewSettings.defaultView || 'resourceTimelineSlidingMonth'
    );
    const [confirmPopupProps, setConfirmPopupProps] = useState({});
    const [productSelection, setProductSelection] = useState({ open: false });
    const [viewSettings, setViewSettings] = useState(props.viewSettings);
    const [hotelSettings, setHotelSettings] = useState(props.hotelSettings);
    const [reservationIcon, setReservationIcon] = useState(
        props.viewSettings.reservationIcon
    );
    const [showYield, setShowYield] = useState(props.viewSettings.showYield);
    const [showHoliday, setShowHoliday] = useState(
        props.viewSettings.showHoliday
    );
    const [showComments, setShowComments] = useState(
        props.viewSettings.showComments
    );
    const [showOccupancy, setShowOccupancy] = useState(
        props.viewSettings.showOccupancy
    );
    const [showReservationNumber, setShowReservationNumber] = useState(
        props.viewSettings.showReservationNumber
    );
    const [showHouseKeeping, setShowHouseKeeping] = useState(
        props.viewSettings.showHouseKeeping
    );
    const [showDisplaySettings, setShowDisplaySettings] = useState(false);
    const [lastChosenDateCalendar, setLastChosenDateCalendar] = useState(
        new Date()
    );
    const text = useTextResources();
    const loader = useLoader();

    const { rebuildVirtualEvents, rebuildDayUseEvents } = props;
    const canEdit = window.optima.user
        ? window.optima.user.ActionIDs.includes(Actions.EditCalendar)
        : true;
    const [selectedPrice, setSelectedPrice] = useState();
    const [showFilter, setShowFilter] = useState(false);
    const { HotelFilterCmp } = props;
    const [customerIcons, setCustomeIcons] = useState({})
    const [showPolicyChangePopup, setShowPolicyChangePopup] = useState(false);
    const [policyDetails, setPolicyDetails] = useState(null);

    const confirmMessages = {
        upgradeRoom: {
            header: text('UpgradeRoomType'),
            body: text('WouldYouLikeToUpgradeThisReservation'),
        },
        changeDates: {
            header: text('ChangeReservationDates'),
            body: text('WouldYouLikeToChangeThisReservationDates'),
        },
    };

    const  GetCustomerIcon = async id=>{
        if (customerIcons[id]) {
            return customerIcons[id]
        } else {
            const icon = await Calendar.GetCustomerIcon(id);
            customerIcons[id] = icon
            setCustomeIcons({...customerIcons})
            return icon
        }
    }

    const currencyFormat = (currencyID) => {
        return {
            format: '%v%s',
            symbol: currencyID ? Currencies[currencyID].symbol : '',
            locale: document.documentElement.lang || 'he-IL',
        };
    };
    
    
    useEffect(() => {
        let calendarApi = calendarRef.current?.getApi();
        if (calendarApi && calendarApi.view.type !== defaultView) {
            calendarApi.changeView(defaultView);
        }
    }, [calendarRef, defaultView]);

    

    useEffect(() => {
        const settings = {
            ...viewSettings,
            reservationIcon,
            showYield,
            showHoliday,
            showComments,
            showOccupancy,
            showHouseKeeping,
            showReservationNumber,
            defaultView,
        };
        if (!deepIs(viewSettings, settings)) {
            setViewSettings(settings);
        }
    }, [
        reservationIcon,
        showYield,
        showHoliday,
        showOccupancy,
        showComments,
        showHouseKeeping,
        showReservationNumber,
        defaultView,
    ]);

    useEffect(() => {
        localStorage.setItem('CalendarSettings', JSON.stringify(viewSettings));
        props.setViewSettings(viewSettings);
    }, [viewSettings]);

    useEffect(() => {
        localStorage.setItem(
            'CalendarHotelSettings',
            JSON.stringify(hotelSettings)
        );
        props.setHotelSettings(hotelSettings);
    }, [hotelSettings]);

    const showTootip = (context, content, position, header) => {
        setTooltipContext(context);
        setTooltipContent(content);
        setTooltipPosition(position);
        setTooltipHeader(header);
        setDisplayTooltip(true);
    };
    const hideTootip = () => {
        setDisplayTooltip(false);
        setTooltipContext(null);
        setTooltipContent(null);
        setTooltipHeader(null);
    };


    /* Resource rendering hooks */

    const resourceGroupLaneContent = useCallback((info) => {
        const calendar = info.view.calendar;
        const resources = calendar.getResources();

        const data = resources.find (resource => resource.extendedProps.group === info.groupValue)?.extendedProps.hotelData;

        if (!data)
            return null;
        // Get slotDuration from the view options
        const slotDuration = info.view.getOption('slotDuration');
        const start = info.view.currentStart;
        const end = info.view.currentEnd;

        // Use moment to iterate over the range by slotDuration
        const slots = [];
        let current = moment(start);
        const mEnd = moment(end); // Exclude the end date
        // slotDuration can be { hours: 1 } or { days: 1 }, etc.
        // Convert slotDuration to moment duration
        const duration = moment.duration(slotDuration);

        while (current.isBefore(mEnd)) {
            const date = current.clone();
            const dayData = data?.find(d=>moment(d.Date).isSame(date, "day"))
            const calendarEvent = viewSettings.showHoliday ? dayData?.CalendarEvent?.Name : null;
            // Create a slot element for each time slot
            slots.push(
                <div key={date.toISOString()} className="resource-group-slot">
                    {calendarEvent && <Popup trigger={<Icon style={{position: "absolute"}} link name="calendar check outline" />} content={calendarEvent} />}
                </div>
            );
            current.add(duration);
        }
        return <div className="resource-group-lane">{slots}</div>;
    }, [viewSettings, defaultView]);


    const resourceRender = useCallback((info) => {
            let room = info.resource.extendedProps.room;
            let hotel = info.resource.extendedProps.hotel;

            // Handle room cells
            if (room) {
                info.el.addEventListener('mouseenter', () => {
                    showTootip(info.el, info.resource.title, 'top center');
                });
                info.el.addEventListener('mouseleave', () => {
                    hideTootip();
                });

                if (room.IsVirtual) {
                    info.el.classList.add('virtual');
                }

                if (viewSettings.showHouseKeeping) {
                    let status = '0';
                    if (_.first(room.RoomHousekeepingStatusLogs)) {
                        status = _.first(
                            room.RoomHousekeepingStatusLogs
                        ).HousekeepingStatusTypeID;
                    }
                    info.el.classList.add(RoomStatusTypes[status]);
                    info.el.classList.add('status-icon');

                    if (!room.IsVirtual) {
                        info.el.addEventListener('click', (e) => {
                            // Detect if the status icon was clicked
                            if (e.target.tagName == 'TD') {
                                (async () => {
                                    try {
                                        if (_.first(room.RoomHousekeepingStatusLogs)) {
                                            status = Number(
                                                _.first(room.RoomHousekeepingStatusLogs)
                                                    .HousekeepingStatusTypeID
                                            );
                                        } else {
                                            status = -1;
                                        }
                                        const logs = await SetRoomHousekeepingStatusLog({
                                            roomID: room.Id,
                                            statusType: (status % 3) + 1,
                                        });
                                        _.assign(room.RoomHousekeepingStatusLogs, logs);
                                        info.el.classList.remove(RoomStatusTypes[status]);
                                        if (_.first(logs)) {
                                            info.el.classList.add(
                                                RoomStatusTypes[_.first(logs).HousekeepingStatusTypeID]
                                            );
                                        }
                                        onSuccess();
                                    } catch (error) {
                                        error.status === 400 ? onWarning(error) : onError(error);
                                    }
                                })();
                            }
                        });
                    }
                }

                info.el.setAttribute('data-roomid', room.Id);
            }
            if (hotel) {
                info.el.setAttribute('data-hotelid', hotel.Id);
            }
    }, [viewSettings] );

    const isWeekend = (fday, date) => {
        const wday = date.weekday();
        switch (fday) {
            case 0:
                return wday == 5 || wday == 6;
            case 1:
                return wday == 6 || wday == 0;
            case 2:
                return wday == 0 || wday == 1;
            default:
                return false;
        }
    };

    const applyCssForDate = () => {
        const locale = document.documentElement.lang || 'he-IL';
        const fday = moment().locale(locale).localeData().firstDayOfWeek();
        const slats = window.document.querySelectorAll('.fc-major');
        slats.forEach((slat) => {
            const date = moment(slat.getAttribute('data-date'));
            slat.classList.add('fc-' + date.format('ddd').toLowerCase());
            if (isWeekend(fday, date)) {
                slat.classList.add('fc-weekend');
            }
            if (date.isSame(moment(), 'd')) {
                slat.classList.add('fc-today');
            }
        });
        const headers = window.document.querySelectorAll('.fc-widget-header');
        headers.forEach((header) => {
            const attrib = header.getAttribute('data-date');
            if (attrib) {
                const date = moment(attrib);
                header.classList.add('fc-' + date.format('ddd').toLowerCase());
                if (isWeekend(fday, date)) {
                    header.classList.add('fc-weekend');
                }
                if (date.isSame(moment(), 'd')) {
                    header.classList.add('fc-today');
                }
            }
        });
        if (locale == "he-IL") {
            const field = window.document.querySelector(".date-title .field")
            field.style.direction = "rtl";
        }
    };

    const datesRender = useCallback((info) => {
        setTitle(info.view.title);
        _.defer(() => {
            applyCssForDate();
        });
    }, []);

    useEffect(() => {
        // Re-apply CSS classes to weekends and today slats
        applyCssForDate();
    }, [viewSettings]);

    const eventMouseEnter = useCallback((info) => {
        const isDragging = info.jsEvent.buttons !== 0;
        if (isBrowser && !isDragging) {
            const reservation = info.event.extendedProps.reservation;
            const maintenance = info.event.extendedProps.maintenance;
            if (reservation) {
                setPopupContext(info.el);
                setPopupReservaion(reservation);
                setShowPopup(true);
            } else if (maintenance) {
                var maintenanceObj = Array.isArray(maintenance) && maintenance.length ?
                    maintenance[0] : maintenance;
                showTootip(
                    info.el,
                    maintenanceObj.Description,
                    'top center',
                    MaintenanceStatusTypes[maintenanceObj.MaintenanceStatusTypeId]
                );
            }
        }
    }, []);

    const eventMouseLeave = useCallback((info) => {
        const reservation = info.event.extendedProps.reservation;
        const maintenance = info.event.extendedProps.maintenance;
        if (isBrowser) {
            if (reservation) {
                setPopupContext(null);
                setShowPopup(false);
            } else if (maintenance) {
                hideTootip();
            }
        }
    }, []);

    const eventClick = useCallback((info) => {
        const events = info.event.extendedProps.events;
        if (events) {
            setEventsPopupContext(info.el.firstElementChild);
            setPopupEvent(info.event);
            setShowPopupEvents(true);
        } else {
            props.eventClick(info);
            setShowPopupEvents(false);
        }
    }, []);

    const eventRender = useCallback(
        (info) => {
            let el = info.el;
            if (info.event.id === 'columndata') {
                const data = info.event.extendedProps.data;
                const addData = (hotel, data, containerEl, width) => {
                    const div = containerEl.appendChild(document.createElement('div'));
                    div.classList.add('hotel-data');
                    if (
                        moment(hotel.HotelParameter.CurrentBusinessDate).isSame(
                            moment(data.Date),
                            'day'
                        )
                    ) {
                        div.classList.add('current-buisness-day');
                    }
                    div.style.width = width;

                    if (data.CalendarEvent || data.Comments) {
                        let extra = document.createElement('div');
                        extra.className = 'extra-data';
                        if (
                            data.CalendarEvent &&
                            data.CalendarEvent.IsActive &&
                            viewSettings.showHoliday
                        ) {
                            let holyday = extra.appendChild(document.createElement('div'));
                            holyday.className = 'icon-star holiday';
                            if (isBrowser) {
                                holyday.addEventListener('mouseenter', () => {
                                    showTootip(holyday, data.CalendarEvent.Name, 'top center');
                                });
                                holyday.addEventListener('mouseleave', () => {
                                    hideTootip();
                                });
                            } else {
                                holyday.addEventListener('touchstart', () => {
                                    showTootip(holyday, data.CalendarEvent.Name, 'top center');
                                });
                                holyday.addEventListener('touchend', () => {
                                    hideTootip();
                                });
                            }
                        } else {
                            extra.appendChild(document.createElement('div')).className =
                                'holiday';
                        }
                        if (data.Comments && viewSettings.showComments) {
                            let comments = extra.appendChild(document.createElement('div'));
                            comments.className = 'icon-chat-37 comments';
                            if (data.CommentsColor && data.CommentsColor !== '') {
                                comments.style.color = data.CommentsColor;
                            }
                            comments.addEventListener('mouseenter', () => {
                                showTootip(comments, data.Comments, 'top center');
                            });
                            comments.addEventListener('mouseleave', () => {
                                hideTootip();
                            });
                        } else {
                            extra.appendChild(document.createElement('div')).className =
                                'comments';
                        }
                        div.appendChild(extra);
                    }
                    if (viewSettings.showYield) {
                        div.appendChild(
                            document.createElement('div')
                        ).innerText = `Y=${data.YieldLevel}`;
                    }
                    if (viewSettings.showOccupancy) {
                        div.appendChild(
                            document.createElement('div')
                        ).innerText = `${data.Occupancy}%`;
                    }
                };

                _.defer(() => {
                    const groupped = _.groupBy(data, 'HotelId');
                    _.forEach(groupped, (cdata, hotelid) => {
                        if (cdata && cdata.length) {
                            // Find the row by hotel id
                            const rows = window.calendar.view.rowComponents;
                            // Find hotel by id
                            const hotel = hotels.find((x) => x.Id === Number(hotelid));

                            const groupRow = rows.find((x) =>
                                x.props.id.endsWith(`-${hotelid}`)
                            );
                            if (groupRow) {
                                const containerEl = groupRow.timeAxisHeightEl;
                                containerEl.innerHTML = '';
                                const slatsContainer =
                                    document.getElementsByClassName('fc-slats')[0];
                                const slats = slatsContainer.querySelectorAll('.fc-major');
                                if (window.calendar.view.type === 'resourceTimelineDay') {
                                    const data = cdata[0];
                                    addData(hotel, data, containerEl, `100%`);
                                } else {
                                    slats.forEach((slat) => {
                                        const date = moment(slat.getAttribute('data-date'));
                                        const data = cdata.find((x) =>
                                            moment(x.Date).isSame(date, 'day')
                                        );
                                        if (data) {
                                            addData(
                                                hotel,
                                                data,
                                                containerEl,
                                                `${slat.clientWidth}px`
                                            );
                                        }
                                    });
                                }
                            }
                        }
                    });
                });
            } else if (info.event.extendedProps.restriction) {
                let restriction = info.event.extendedProps.restriction;
                let div = document.createElement('div');
                div.className = 'restriction-container';
                if (restriction.MinDays || restriction.MaxDays) {
                    let minmax = document.createElement('div');
                    minmax.className = 'min-max';
                    minmax.innerText = `${restriction.MinDays || 0}/${restriction.MaxDays || '~'
                        }`;
                    div.appendChild(minmax);
                }
                if (restriction.RestrictionType) {
                    let openclose = document.createElement('div');
                    openclose.className = 'open-close';
                    let image = document.createElement('img');
                    image.src = RestrictionIcons[restriction.RestrictionType - 1];
                    openclose.appendChild(image);
                    div.appendChild(openclose);
                }
                el.appendChild(div);
            } else if (info.event.extendedProps.pricedata) {
                const pricedata = info.event.extendedProps.pricedata;
                const price = pricedata.TotalAmount;
                if (price) {
                    let div = document.createElement('div');
                    div.className = 'price-container';
                    div.addEventListener('mouseenter', (e) => {
                        setSelectedPrice({
                            el: e.target,
                            pricedata,
                        });
                    });
                    div.addEventListener('mouseleave', () => {
                        setSelectedPrice(null);
                    });
                    const cf = currencyFormat(pricedata.CurrencyId);
                    div.innerHTML = `<span>${formatCurrency(pricedata.TotalAmount, cf)}</span>`;
                    el.appendChild(div);
                }
            } else {
                // Handle regular events
                const reservation = info.event.extendedProps.reservation;

                if (reservation && reservation.Status) {
                    var className = reservation.Status.Name.replace(/\s/g, '').toLowerCase()+'-calendar';
                    el.classList.add(className);
                }

                if (reservation && !info.event.extendedProps.Virtual) {
                    // Do not disaplay single day reservations in month view
                    if (
                        ['resourceTimelineSlidingMonth', 'resourceTimelineMonth'].includes(
                            info.view.type
                        )
                    ) {
                        if (
                            moment(reservation.CheckInDate).isSame(
                                reservation.CheckOutDate,
                                'day'
                            )
                        ) {
                            return false;
                        }
                    }

                    if (
                        ['resourceTimelineBiWeek', 'resourceTimelineWeek'].includes(
                            info.view.type
                        )
                    ) {
                        if (
                            moment(reservation.CheckInDate).isSame(
                                reservation.CheckOutDate,
                                'day'
                            )
                        ) {
                            el.classList.add('hideTitle');
                        }
                    }

                    switch (reservationIcon) {
                        case '24h':
                            if (
                                moment(reservation.CreateDate).isBetween(
                                    moment().subtract(1, 'days'),
                                    moment()
                                )
                            ) {
                                el.classList.add('last24hours');
                                el.classList.add('hasicon');
                            }
                            break;
                        case 'balance':
                            if (info.event.extendedProps.totals) {
                                el.classList.add('balance');
                                const totals = info.event.extendedProps.totals;
                                const balance =
                                    totals.TotalAmountDebitAfterTax +
                                    totals.TotalAmountCreditAfterTax;
                                const notinvoiced =
                                    totals.TotalAmountDebitBeforeTax -
                                    totals.TotalAmountDebitBeforeTaxHasInvoice;
                                if (Math.abs(notinvoiced) < 0.01) {
                                    el.classList.add('invoiced');
                                } else if (Math.abs(balance) < 0.01) {
                                    el.classList.add('nothing');
                                } else if (Math.abs(totals.TotalAmountCreditAfterTax) > 0.01) {
                                    el.classList.add('partial');
                                } else {
                                    el.classList.add('full');
                                }
                                el.classList.add('hasicon');
                            }
                            break;
                        case 'agent':
                            el.classList.add('agent');
                            el.classList.add('hasicon');
                            if (reservation.Agent) {
                                if (reservation.Agent.HasIcon) {
                                    const icon = document.createElement('img');
                                    GetCustomerIcon(reservation.Agent.Id).then(result=>{
                                        icon.src = result;
                                    });
                                    icon.className = 'agent-icon';
                                    el.appendChild(icon);
                                }
                                if (reservation.Agent.Color) {
                                    el.style.backgroundColor = reservation.Agent.Color;
                                    el.style.color = getContrastYIQ(reservation.Agent.Color);
                                }
                            }
                            break;
                        case 'company':
                            el.classList.add('company');
                            el.classList.add('hasicon');
                            if (reservation.Company) {
                                if (reservation.Company.HasIcon) {
                                    const icon = document.createElement('img');
                                    GetCustomerIcon(reservation.Company.Id).then(result=>{
                                        icon.src = result;
                                    });
                                    icon.className = 'company-icon';
                                    el.appendChild(icon);
                                }
                                if (reservation.Company.Color) {
                                    el.style.backgroundColor = reservation.Company.Color;
                                    el.style.color = getContrastYIQ(reservation.Company.Color);
                                }
                            }
                            break;
                        case 'confirmation':
                            if (reservation.ConfirmationDate) {
                                el.classList.add('confirmed');
                                el.classList.add('hasicon');
                            }
                            break;
                        case 'documents': {
                            if (
                                reservation?.Images?.length
                            ) {
                                el.classList.add('documents', 'hasicon');
                                el.setAttribute(
                                    'data-documents',
                                    reservation.Images.length
                                );
                            }
                        }
                    }
                } else if (
                    info.event.extendedProps.events &&
                    !info.event.extendedProps.Virtual
                ) {
                    const events = info.event.extendedProps.events;
                    el.classList.add('hideTitle');
                    if (
                        ['resourceTimelineSlidingMonth', 'resourceTimelineMonth'].includes(
                            info.view.type
                        )
                    ) {
                        info.el.innerHTML = `<div class="icon">${events.length}</div>`;
                    } else {
                        return null;
                    }
                }
                el.setAttribute('data-event-id', info.event.id);
            }
        },
        [viewSettings]
    );

    const eventResize = useCallback(
        async (info) => {
            if (!(await confirm(confirmMessages.changeDates))) {
                info.revert();
                return;
            }

            if (info.event.extendedProps.reservation) {
                let reservation = structuredClone(info.event.extendedProps.reservation)
                const resource = info.event.getResources()[0];
                const room = resource.extendedProps.room;
                const roomId = room.Id;
                RebuildReservation(
                    reservation,
                    info.event.start,
                    info.event.end,
                    roomId
                );

                try {
                    loader(true);
                    let updated = await props.saveReservation(reservation);
                    /*PAY ATTENTION- currently refetching reservation from server instead of using 
                      the return value of saveReservation due to differences in Id capitalizations 
                      between OP2 and OP3 - should be changed once saveRes works with OP3 */
                    updated = await GetSingleReservation(updated);
                    info.event.setExtendedProp('reservation', updated);
                    rebuildVirtualEvents();
                    onSuccess();
                } catch (error) {
                    error.status === 400 ? onWarning(error) : onError(error);
                    info.revert();
                } finally {
                    loader(false);
                }
            } else if (info.event.extendedProps.maintenance) {
                var maintenanceObj = Array.isArray(info.event.extendedProps.maintenance) && info.event.extendedProps.maintenance.length ?
                    info.event.extendedProps.maintenance[0] : info.event.extendedProps.maintenance;
                const maintenance = {
                    ...maintenanceObj,
                    StartDate: info.event.start,
                    EndDate: info.event.end,
                };
                try {
                    loader(true);
                const updated = maintenance.Id === 0
                    ? await RoomPlannedMaintenance.AddRoomPlannedMaintenances([maintenance])
                    : await RoomPlannedMaintenance.UpdateRoomPlannedMaintenances([maintenance]);
                    info.event.setExtendedProp('maintenance', updated[0]);
                    rebuildVirtualEvents();
                    onSuccess();
                } catch (error) {
                    error.status === 400 ? onWarning(error) : onError(error);
                    info.revert();
                } finally {
                    loader(false);
                }
            } else {
                info.revert();
            }
        },
        [rebuildVirtualEvents]
    );

    const confirm = (currMessage) => {
        return new Promise((resolve) => {
            setConfirmPopupProps({
                open: true,
                header: currMessage.header,
                body: currMessage.body,
                onCancel: () => {
                    setConfirmPopupProps({
                        open: false,
                        header: null,
                        body: null,
                    });
                    resolve(false);
                },
                onConfirm: () => {
                    setConfirmPopupProps({
                        open: false,
                        header: null,
                        body: null,
                    });
                    resolve(true);
                },
            });
        });
    };

    const getNewProductID = (reservation) => {
        const spec = {
            HotelID: reservation.HotelId,
            IncludeDayPricing: true,
            IsDayUse: moment(reservation.CheckInDate).isSame(
                reservation.CheckOutDate,
                'day'
            ),
            MaxAdults: reservation.Adults,
            MaxChildren: reservation.Children,
            MaxInfants: reservation.Infants,
            RoomTypeId: reservation.RoomTypeId,
            StartDate: reservation.CheckInDate,
            EndDate: reservation.CheckOutDate,
            AvoidAllRestriction: true,
            IsNewAlgo: true,
        };

        return new Promise((resolve) => {
            setProductSelection({
                open: true,
                spec: spec,
                cancel: () => {
                    setProductSelection({
                        open: false,
                    });
                    resolve(null);
                },
                select: (productId) => {
                    setProductSelection({
                        open: false,
                    });
                    resolve(productId);
                },
            });
        });
    };

    const eventDragStart = useCallback((info) => {
        eventMouseLeave(info);
    }, []);

    const eventDrop = useCallback(async (info) => {
        if (info.event.extendedProps.reservation) {
            let reservation = structuredClone(info.event.extendedProps.reservation);
            if (info.delta.days != 0) {
                // Dates where changed, update daily accomodations and addons with new dates

                if (!(await confirm(confirmMessages.changeDates))) {
                    info.revert();
                    return;
                }

                if (moment(info.event.start).isBefore(moment())) {
                    onWarning('Cannot move reservation to past date!');
                    info.revert();
                    return;
                }
                reservation.CheckInDate = info.event.start;
                reservation.CheckOutDate = info.event.end;
                _.forEach(
                    reservation.ReservationDailyAccommodations,
                    (accommodation) => {
                        accommodation.Date = moment(accommodation.Date)
                            .add(info.delta.days, 'days')
                            .toDate();
                    }
                );
                _.forEach(reservation.ReservationDailyAddons, (addon) => {
                    const getAdjustedDate = (Date) =>
                        moment(Date).add(info.delta.days, 'days').toDate();

                    addon.Date = getAdjustedDate(addon.Date);
                    addon.ConsumptionDate = getAdjustedDate(addon.ConsumptionDate);
                });
            }
            if (info.newResource) {
                // Room and possibly roomtype was changed, update reservation and daily items with new data
                if (
                    info.oldResource.extendedProps.hotelId !==
                    info.newResource.extendedProps.hotelId
                ) {
                    onWarning('Cannot move reservation to a different hotel!');
                    info.revert();
                    return;
                }
                const oldRoom = info.oldResource.extendedProps.room;
                const newRoom = info.newResource.extendedProps.room;
                if (oldRoom.TypeId === newRoom.TypeId) {
                    // Same roomtype, no upgrate, so just update the correct room id
                    reservation.RoomId = newRoom.Id;
                    _.forEach(
                        reservation.ReservationDailyAccommodations,
                        (accomodation) => {
                            accomodation.RoomId = newRoom.Id;
                        }
                    );
                } else {
                    if (await confirm(confirmMessages.upgradeRoom)) {
                        reservation.UpgradedFromRoomTypeId = reservation.RoomTypeId;
                        reservation.RoomTypeId = newRoom.TypeId;
                        reservation.RoomId = newRoom.Id;
                        _.forEach(
                            reservation.ReservationDailyAccommodations,
                            (accomodation) => {
                                accomodation.UpgradedFromRoomTypeId = accomodation.RoomTypeId;
                                accomodation.RoomTypeId = newRoom.TypeId;
                                accomodation.RoomId = newRoom.Id;
                            }
                        );
                    } else {
                        const isPostBooking =
                            reservation.ReservationDailyAccommodations.find(
                                (x) => x.FolioTransactionID > 0
                            );
                        if (isPostBooking) {
                            info.revert();
                            return;
                        }

                        reservation.RoomTypeId = newRoom.TypeId;
                        reservation.RoomId = newRoom.Id;
                        // Show product select dialog
                        const newProductId = await getNewProductID(reservation);
                        if (newProductId) {
                            reservation.ProductId = newProductId;
                            // Remove any daily values to rebuild by the server when changing product
                            reservation.ReservationDailyAccommodations = [];
                            reservation.ReservationDailyAddons = [];
                        } else {
                            info.revert();
                            return;
                        }
                    }
                }
            }
            // Update ETA/ETD in case event only moved within same day
            (reservation.Eta = moment(info.event.start).format('HH:mm:ss')),
                (reservation.Edt = moment(info.event.end).format('HH:mm:ss'));
            try {
                // Updates reservation policy if changed
                const newPolicyResult = await checkForPolicyChange(reservation);
                if (newPolicyResult) {
                    reservation.ReservationPolicyId = newPolicyResult.Id;
                    reservation.ReservationPolicy = newPolicyResult;
                }

                loader(true);
                let updated = await props.saveReservation(reservation);
                /*PAY ATTENTION- currently refetching reservation from server instead of using 
                  the return value of saveReservation due to differences in Id capitalizations 
                  between OP2 and OP3 - should be changed once saveRes works with OP3 */
                updated = await GetSingleReservation(updated);
                if (
                    updated.ProductID !== info.event.extendedProps.reservation.ProductId
                ) {
                    // Reservation product (and hence price) has changed so we need to update the totals
                    const totals = await GetReservationTotals({
                        ReservationIDs: [updated.Id],
                        HotelId: updated.HotelId,
                        TargetCurrencyID: updated.CurrencyId,
                    });
                    if (totals && totals.length) {
                        info.event.setExtendedProp('totals', totals[0]);
                    }
                }
                info.event.setExtendedProp('reservation', updated);
                rebuildDayUseEvents();
                rebuildVirtualEvents();
                onSuccess();
            } catch (error) {
                error.status === 400 ? onWarning(error) : onError(error);
                info.revert();
            } finally {
                loader(false);
            }
        } else if (info.event.extendedProps.maintenance) {
            var maintenanceObj = Array.isArray(info.event.extendedProps.maintenance) && info.event.extendedProps.maintenance.length ?
                info.event.extendedProps.maintenance[0] : info.event.extendedProps.maintenance;
            const maintenance = {
                ...maintenanceObj,
                StartDate: info.event.start,
                EndDate: info.event.end,
            };
            if (info.newResource) {
                // Room and possibly roomtype was changed, update reservation and daily items with new data
                if (
                    info.oldResource.extendedProps.hotelId !==
                    info.newResource.extendedProps.hotelId
                ) {
                    onWarning('Cannot move maintenance to a different hotel!');
                    info.revert();
                    return;
                }
                const newRoom = info.newResource.extendedProps.room;
                maintenance.RoomId = newRoom.Id;
                maintenance.RoomTypeId = newRoom.TypeId;
            }
            try {
                loader(true);
                const updated = maintenance.Id === 0
                    ? await RoomPlannedMaintenance.AddRoomPlannedMaintenances([maintenance])
                    : await RoomPlannedMaintenance.UpdateRoomPlannedMaintenances([maintenance]);
                info.event.setExtendedProp('maintenance', updated[0]);
                rebuildVirtualEvents();
                onSuccess();
            } catch (error) {
                error.status === 400 ? onWarning(error) : onError(error);
                info.revert();
            } finally {
                loader(false);
            }
        } else {
            info.revert();
        }
    }, []);

    

    const renderGroupResource = useCallback((el, value) => {
        if (value) {
            let s = value.split('-');
            const hotelid = Number(s[1]);
            const hotel = hotels.find((x) => x.Id === Number(hotelid));
            let showSettings = hotel.HotelFeatures.find(
                (feature) => feature.FeatureID === 12
            );
            const settings = hotelSettings[hotelid];
            el.classList.add('hotel-info');
            el.setAttribute('data-hotelid', hotelid);

            if (settings && settings.rateName) {
                let rate = el.appendChild(document.createElement('div'));
                rate.classList.add('rate-name');
                rate.innerHTML = `${text('Rate')}: ${settings.rateName}`;
            }
            // if (showSettings) {
            //     let icon = el.appendChild(document.createElement('div'));
            //     icon.className = 'view-settings';
            //     ReactDOM.render(
            //         <LanguageContext.Provider value={language}>
            //             <SettingsPopup HotelID={hotelid} onChange={setHotelSettings} />
            //         </LanguageContext.Provider>,
            //         icon
            //     );
            // }
        }
    }, []);

    const datesSet = useCallback((info) => {
        setTitle(info.view.title);
    }, []);

    const prev = () => {
        let calendarApi = calendarRef.current.getApi();
        if (calendarApi.view.type === 'resourceTimelineSlidingMonth') {
            let date = calendarApi.getDate();
            gotoDate(moment(date).subtract(1, 'month').toDate(), false);
        } else {
            calendarApi.prev();
        }
    };

    const next = () => {
        let calendarApi = calendarRef.current.getApi();
        if (calendarApi.view.type === 'resourceTimelineSlidingMonth') {
            let date = calendarApi.view.currentEnd; // calendarApi.getDate();
            gotoDate(moment(date).toDate(), false);
        } else {
            calendarApi.next();
        }
    };

    const changeView = (view) => {
        setDefaultView(view);
        let calendarApi = calendarRef.current.getApi();
        if (lastChosenDateCalendar && view === 'resourceTimelineDay') {
            calendarApi.gotoDate(lastChosenDateCalendar);
        } else {
            calendarApi.gotoDate(defaultDate(view));
        }
    };

    const gotoDate = (date, slide = true) => {
        if (date) {
            let calendarApi = calendarRef.current.getApi();
            setLastChosenDateCalendar(date);
            if (calendarApi.view.type === 'resourceTimelineSlidingMonth' && slide) {
                date = moment(date).subtract(4, 'days').startOf('day').toDate();
                calendarApi.gotoDate(date);
                // Force re-render by setting the view
                calendarApi.changeView('resourceTimelineSlidingMonth');
            } else {
                calendarApi.gotoDate(date);
            }
        }
    };

    const defaultMonthLabelFormat = useMemo(()=>[{ weekday: 'short' }, { day: '2-digit' }], []);
    const defaultWeekLabelFormat = useMemo(()=>[{ weekday: 'short' }, { day: '2-digit' }], []);
    const defaultDuration = useMemo(()=>({ hours: 24,}), []);

    const selectAllow = useCallback((info) => {
        const selRange = moment.range(
            info.start,
            moment(info.end).subtract(1, 'minute')
        );
        const events = info.resource.getEvents();
        let result = true;
        events.forEach((event) => {
            const eventRange = moment.range(
                moment(event.start).endOf('day'),
                moment(event.end).startOf('day')
            );
            const { reservation } = event.extendedProps;
            if (window.calendar.view.type === 'resourceTimelineDay') {
                const dayEventRange = moment.range(
                    moment(event.start),
                    moment(event.end)
                );
                result = result && !selRange.overlaps(dayEventRange);
            } else if (!reservation || !reservation.IsDayUse) {
                result = result && !selRange.overlaps(eventRange);
            }
        });
        return result;
    }, []);

    const eventOverlap = useCallback((stillEvent) => {
        return stillEvent.rendering === 'background';
    }, []);

    const checkForPolicyChange = async (reservation) => {
        if (!reservation.Id || !reservation.ReservationPolicyId) {
            return null;
        }

        try {
            // Get the new product details to check its policy
            const products = await Products.Search({
                Id: reservation.ProductId,
                HotelId: reservation.HotelId,
                FromDate: moment(reservation.CheckInDate).startOf('day').toISOString(),
                ToDate: moment(reservation.CheckOutDate).startOf('day').toISOString(),
                "Pax.Adults": reservation.Adults || 0,
                "Pax.Children": reservation.Children || 0,
                "Pax.Infants": reservation.Infants || 0,
                Includes: ["ReservationPolicy",
                    "ProductPrices",
                    "DailyProductPrices"],
                LanguageId: window.optima?.user?.UserSettings[0]?.LanguageID,
                IncludeReservationPolicyAutoDescription: true,
            });

            if (products && products.length > 0) {
                const newProduct = products[0];

                // Check if the policy has changed
                if (newProduct.ReservationPolicyId &&
                    newProduct.ReservationPolicyId !== reservation.ReservationPolicyId) {

                    // Show the policy change confirmation popup
                    setPolicyDetails(newProduct.ReservationPolicy);
                    setShowPolicyChangePopup(true);

                    // Wait for user decision
                    return new Promise((resolve) => {
                        window.policyChangeResolve = resolve;
                    });
                }
            }
            return null;
        } catch (error) {
            console.error('Error checking policy change:', error);
            return null;
        }
    };

    const handleApplyNewPolicy = useCallback(() => {
        setShowPolicyChangePopup(false);
        if (window.policyChangeResolve) {
            window.policyChangeResolve(policyDetails);
            window.policyChangeResolve = null;
        }
    }, [policyDetails]);

    const handleKeepCurrentPolicy = useCallback(() => {
        setShowPolicyChangePopup(false);
        setPolicyDetails(null);
        if (window.policyChangeResolve) {
            window.policyChangeResolve(null);
            window.policyChangeResolve = null;
        }
    }, []);

    const defaultDate = (view) => {
        if ((view || defaultView) === 'resourceTimelineSlidingMonth') {
            return moment().subtract(4, 'days').startOf('day').toDate();
        } else {
            return moment().startOf('day').toDate();
        }
    };

    return (
        <React.Fragment>
            {isBrowser ? (
                <Grid columns='3' verticalAlign='middle'>
                    <Grid.Row style={{ marginBottom: '.5rem' }}>
                        <Grid.Column>
                            <HotelFilterCmp />
                        </Grid.Column>
                        <Grid.Column textAlign='center' className='date-title'>
                            <Icon name='arrow left' link onClick={prev} fitted />
                            <DatePicker
                                clearable={false}
                                icon=''
                                reaonly
                                id='dateinput'
                                type='basic'
                                label={title}
                                onChange={gotoDate}
                            />
                            <Icon name='arrow right' link onClick={next} fitted />
                        </Grid.Column>
                        <Grid.Column textAlign='right'>
                            <Menu secondary compact>
                                <Dropdown
                                    item
                                    value={defaultView}
                                    options={[
                                        {
                                            value: 'resourceTimelineDay',
                                            key: 'day',
                                            text: text('Day'),
                                        },
                                        {
                                            value: 'resourceTimelineWeek',
                                            key: 'week',
                                            text: text('Week'),
                                        },
                                        {
                                            value: 'resourceTimelineBiWeek',
                                            key: 'biweek',
                                            text: text('TwoWeeks'),
                                        },
                                        {
                                            value: 'resourceTimelineSlidingMonth',
                                            key: 'current',
                                            text: text('Month'),
                                        },
                                        {
                                            value: 'resourceTimelineMonth',
                                            key: 'month',
                                            text: text('CurrentMonth'),
                                        },
                                    ]}
                                    onChange={(e, data) => changeView(data.value)}
                                />
                                <Dropdown
                                    item
                                    value={reservationIcon}
                                    options={[
                                        {
                                            value: 'none',
                                            key: 'none',
                                            text: text('Default'),
                                        },
                                        {
                                            value: '24h',
                                            key: '24h',
                                            text: text('Last24Hours'),
                                        },
                                        {
                                            value: 'balance',
                                            key: 'balance',
                                            text: text('PaymentStatus'),
                                        },
                                        {
                                            value: 'agent',
                                            key: 'agent',
                                            text: text('Agent'),
                                        },
                                        {
                                            value: 'company',
                                            key: 'company',
                                            text: text('Company'),
                                        },
                                        {
                                            value: 'confirmation',
                                            key: 'confirmation',
                                            text: text('Confirmation'),
                                        },
                                        {
                                            value: 'documents',
                                            key: 'documents',
                                            text: text('Documents'),
                                        },
                                    ]}
                                    onChange={(e, { value }) => setReservationIcon(value)}
                                />
                                <Dropdown
                                    direction='left'
                                    item
                                    text={text('ViewSettings')}
                                    closeOnChange={false}
                                    closeOnBlur={false}
                                    value=''
                                    options={[
                                        {
                                            text: (
                                                <Checkbox
                                                    checked={showYield}
                                                    label={text('Yield')}
                                                    onChange={(e, { checked }) => setShowYield(checked)}
                                                />
                                            ),
                                            key: 'Yield',
                                        },
                                        {
                                            text: (
                                                <Checkbox
                                                    checked={showHoliday}
                                                    label={text('Holidays')}
                                                    onChange={(e, { checked }) => setShowHoliday(checked)}
                                                />
                                            ),
                                            key: 'Holidays',
                                        },
                                        {
                                            text: (
                                                <Checkbox
                                                    checked={showComments}
                                                    label={text('Comments')}
                                                    onChange={(e, { checked }) =>
                                                        setShowComments(checked)
                                                    }
                                                />
                                            ),
                                            key: 'Comments',
                                        },
                                        {
                                            text: (
                                                <Checkbox
                                                    checked={showOccupancy}
                                                    label={text('Occupancy')}
                                                    onChange={(e, { checked }) =>
                                                        setShowOccupancy(checked)
                                                    }
                                                />
                                            ),
                                            key: 'Occupancy',
                                        },
                                        {
                                            text: (
                                                <Checkbox
                                                    checked={showHouseKeeping}
                                                    label={text('Housekeeping')}
                                                    onChange={(e, { checked }) =>
                                                        setShowHouseKeeping(checked)
                                                    }
                                                />
                                            ),
                                            key: 'HouseKeeping',
                                        },
                                        {
                                            text: (
                                                <Checkbox
                                                    checked={showReservationNumber}
                                                    label={text('ReservationNumber')}
                                                    onChange={(e, { checked }) =>
                                                        setShowReservationNumber(checked)
                                                    }
                                                />
                                            ),
                                            key: 'ReservationNumber',
                                        },
                                        // {
                                        //     text: text("DisplaySettings"),
                                        //     onClick: ()=>setShowDisplaySettings(true),
                                        //     key: "DisplaySettings"
                                        // }
                                    ]}
                                />
                            </Menu>
                        </Grid.Column>
                    </Grid.Row>
                </Grid>
            ) : (
                <Grid
                    columns='1'
                    verticalAlign='middle'
                    style={{ marginBottom: '.5rem' }}
                >
                    <Grid.Row
                        className='mobile-date-title'
                        columns={2}
                        textAlign='center'
                    >
                        <Grid.Column width={2}>
                            <Button
                                size='mini'
                                icon='filter'
                                onClick={() => setShowFilter((s) => !s)}
                            />
                        </Grid.Column>
                        <Grid.Column textAlign='center' className='date-title' width={14}>
                            <Icon name='arrow left' link onClick={prev} fitted />
                            <DatePicker
                                clearable={false}
                                icon=''
                                transparent
                                readonly
                                id='dateinput'
                                type='basic'
                                label={title}
                                onChange={gotoDate}
                            />
                            <Icon name='arrow right' link onClick={next} fitted />
                        </Grid.Column>
                    </Grid.Row>
                    {showFilter && (
                        <Grid.Row className='calendar-setting-mobile'>
                            <Grid.Column textAlign='center'>
                                <Menu secondary compact size='tiny'>
                                    <Dropdown
                                        upward={false}
                                        item
                                        value={defaultView}
                                        options={[
                                            {
                                                value: 'resourceTimelineDay',
                                                key: 'day',
                                                text: text('Day'),
                                            },
                                            {
                                                value: 'resourceTimelineWeek',
                                                key: 'week',
                                                text: text('Week'),
                                            },
                                            {
                                                value: 'resourceTimelineBiWeek',
                                                key: 'biweek',
                                                text: text('TwoWeeks'),
                                            },
                                            {
                                                value: 'resourceTimelineSlidingMonth',
                                                key: 'current',
                                                text: text('Month'),
                                            },
                                            {
                                                value: 'resourceTimelineMonth',
                                                key: 'month',
                                                text: text('CurrentMonth'),
                                            },
                                        ]}
                                        onChange={(e, data) => changeView(data.value)}
                                    />
                                    <Dropdown
                                        upward={false}
                                        item
                                        value={reservationIcon}
                                        options={[
                                            {
                                                value: 'none',
                                                key: 'none',
                                                text: text('Default'),
                                            },
                                            {
                                                value: '24h',
                                                key: '24h',
                                                text: text('Last24Hours'),
                                            },
                                            {
                                                value: 'balance',
                                                key: 'balance',
                                                text: text('OweMoney'),
                                            },
                                            {
                                                value: 'agent',
                                                key: 'agent',
                                                text: text('Agent'),
                                            },
                                            {
                                                value: 'company',
                                                key: 'company',
                                                text: text('Company'),
                                            },
                                        ]}
                                        onChange={(e, { value }) => setReservationIcon(value)}
                                    />
                                    <Dropdown
                                        upward={false}
                                        item
                                        text={text('ViewSettings')}
                                        closeOnChange={false}
                                        closeOnBlur={false}
                                        value=''
                                        direction='left'
                                        options={[
                                            {
                                                text: (
                                                    <Checkbox
                                                        checked={showYield}
                                                        label={text('Yield')}
                                                        onChange={(e, { checked }) => setShowYield(checked)}
                                                    />
                                                ),
                                                key: 'Yield',
                                            },
                                            {
                                                text: (
                                                    <Checkbox
                                                        checked={showHoliday}
                                                        label={text('Holidays')}
                                                        onChange={(e, { checked }) =>
                                                            setShowHoliday(checked)
                                                        }
                                                    />
                                                ),
                                                key: 'Holidays',
                                            },
                                            {
                                                text: (
                                                    <Checkbox
                                                        checked={showComments}
                                                        label={text('Comments')}
                                                        onChange={(e, { checked }) =>
                                                            setShowComments(checked)
                                                        }
                                                    />
                                                ),
                                                key: 'Comments',
                                            },
                                            {
                                                text: (
                                                    <Checkbox
                                                        checked={showOccupancy}
                                                        label={text('Occupancy')}
                                                        onChange={(e, { checked }) =>
                                                            setShowOccupancy(checked)
                                                        }
                                                    />
                                                ),
                                                key: 'Occupancy',
                                            },
                                            {
                                                text: (
                                                    <Checkbox
                                                        checked={showHouseKeeping}
                                                        label={text('Housekeeping')}
                                                        onChange={(e, { checked }) =>
                                                            setShowHouseKeeping(checked)
                                                        }
                                                    />
                                                ),
                                                key: 'HouseKeeping',
                                            },
                                            {
                                                text: (
                                                    <Checkbox
                                                        checked={showReservationNumber}
                                                        label={text('ReservationNumber')}
                                                        onChange={(e, { checked }) =>
                                                            setShowReservationNumber(checked)
                                                        }
                                                    />
                                                ),
                                                key: 'ReservationNumber',
                                            },
                                            // {
                                            //     text: text("DisplaySettings"),
                                            //     onClick: ()=>setShowDisplaySettings(true),
                                            //     key: "DisplaySettings"
                                            // }
                                        ]}
                                    />
                                </Menu>
                            </Grid.Column>
                        </Grid.Row>
                    )}
                </Grid>
            )}

            <div className='calendar'>
                <FullCalendar
                    ref={calendarRef}
                    schedulerLicenseKey='GPL-My-Project-Is-Open-Source'
                    plugins={useMemo(() => [resourceTimelinePlugin, interactionPlugin], [])}
                    defaultView={defaultView}
                    selectable={canEdit}
                    editable={canEdit}
                    locales={useMemo(() => [enLocale, heLocale], [])}
                    nowIndicator={true}
                    locale = {window.user?.UserSettings?.[0]?.Language?.Code}
                    // defaultDate={defaultDate()}
                    // displayEventTime={false}
                    // //selectOverlap={selectOverlap}
                    // selectAllow={selectAllow}
                    
                    // eventOverlap={eventOverlap}

                    headerToolbar={false}
                    views={useMemo(() => ({
                        resourceTimelineDay: {
                            slotLabelFormat: [
                                { day: '2-digit', weekday: 'long' },
                                { hour: '2-digit', hour12: false, minute: '2-digit' },
                            ],
                            slotDuration: { hours: 1 },
                            slotLabelInterval: { hours: 1 },
                        },
                        resourceTimelineWeek: {
                            slotLabelFormat: defaultWeekLabelFormat,
                            slotDuration: defaultDuration,
                            slotLabelInterval: defaultDuration,
                        },
                        resourceTimelineBiWeek: {
                            slotLabelFormat: defaultWeekLabelFormat,
                            type: 'resourceTimeline',
                            duration: { days: 14 },
                            slotDuration: defaultDuration,
                            slotLabelInterval: defaultDuration,
                        },
                        resourceTimelineMonth: {
                            slotLabelFormat: defaultMonthLabelFormat,
                            slotDuration: defaultDuration,
                            slotLabelInterval: defaultDuration,
                        },
                        resourceTimelineSlidingMonth: {
                            slotLabelFormat: defaultMonthLabelFormat,
                            type: 'resourceTimeline',
                            dayCount: 30,
                            slotDuration: defaultDuration,
                            slotLabelInterval: defaultDuration,
                        },
                    }), [defaultDuration, defaultMonthLabelFormat, defaultWeekLabelFormat])}
                    height='100%'
                    themeSystem='default'
                    datesSet={datesSet}
                    // /* Selection and resizing */
                    select={props.select}
                    selectMinDistance={10}
                    // /* Render hooks */
                    
                    // eventRender={eventRender}
                    // datesRender={datesRender}
                    
                    // /* Event callbacks */
                    // eventMouseEnter={eventMouseEnter}
                    // eventMouseLeave={eventMouseLeave}
                    // eventResize={eventResize}
                    // eventDrop={eventDrop}
                    // eventDragStart={eventDragStart}
                    // eventResizeStart={eventMouseLeave}
                    // eventClick={eventClick}
                    eventSources={useMemo(() => [
                        {
                            id: 'main',
                            events: props.eventsFunction,
                        },
                    //     {
                    //         id: 'daydata',
                    //         events: props.dayDataFunction,
                    //     },
                    //     {
                    //         id: 'pricedata',
                    //         events: props.priceDataFunction,
                    //     },
                    //      {
                    //          id: 'coldata',
                    //          events: props.colDataFunction,
                    //      },
                    ], [props.eventsFunction])}
                    /* Resources */
                    refetchResourcesOnNavigate={true}
                    resources={props.resourcesFunction}
                    resourceAreaHeaderContent ={text("Rooms")}
                    resourceAreaWidth={isMobileOnly ? 200 : 320}
                    resourceGroupField='group'
                    resourceGroupLaneContent ={resourceGroupLaneContent}
                    resourcesInitiallyExpanded={false}
                />
            </div>
            <SchedulerPopup
                open={showPopup}
                context={popupContext}
                reservation={popupReservaion}
            />
            <EventsPopup
                open={showPopupEvents}
                context={eventspopupContext}
                event={popupEvent}
                onClose={() => {
                    setShowPopupEvents(false);
                    setEventsPopupContext(null);
                }}
                onEventClick={eventClick}
            />
            <Popup
                open={displayTooltip}
                context={tooltipContext}
                header={tooltipHeader}
                content={tooltipContent}
                position={tooltipPosition}
            />
            {selectedPrice && (
                <Popup
                    open={true}
                    context={selectedPrice.el}
                    header='Prices'
                    content={() => {
                        const pricedata = selectedPrice.pricedata;
                        const cf = currencyFormat(pricedata.CurrencyId);
                        if (pricedata.RoomPriceBeforeTax) {
                            return (
                                <Popup.Content>
                                    <div>
                                        {text('PriceForRoom')}:{' '}
                                        {formatCurrency(pricedata.TotalAmount, cf)}
                                    </div>
                                </Popup.Content>
                            );
                        } else if (pricedata.IsVilla) {
                            return (
                                <Popup.Content>
                                    <div>
                                        {text('VillaPrice').replace('%1', pricedata.Persons)}:{' '}
                                        {formatCurrency(pricedata.TotalAmount, cf)}
                                    </div>
                                </Popup.Content>
                            );
                        } else {
                            return (
                                <Popup.Content>
                                    <div>
                                        {text('SinglePrice')}:{' '}
                                        {formatCurrency(
                                            pricedata.SinglePriceBeforeTax == pricedata.TotalAmount
                                                ? pricedata.SinglePriceBeforeTax
                                                : pricedata.SinglePriceAfterTax,
                                            cf
                                        )}
                                    </div>
                                    <div>
                                        {text('DoublePrice')}:{' '}
                                        {formatCurrency(
                                            pricedata.SinglePriceBeforeTax == pricedata.TotalAmount
                                                ? pricedata.DoublePriceBeforeTax
                                                : pricedata.DoublePriceAfterTax,
                                            cf
                                        )}
                                    </div>
                                </Popup.Content>
                            );
                        }
                    }}
                />
            )}
            <Confirm
                dimmer='inverted'
                confirmButton={{ content: 'Yes', positive: true }}
                cancelButton={{ content: 'No', negative: true }}
                {...confirmPopupProps} //Operational properties and funcs
            />
            <SelectProductPopup
                open={productSelection.open}
                spec={productSelection.spec}
                onCancel={productSelection.cancel}
                onSelect={productSelection.select}
            />
            <CalendarDisplaySettingsPopup
                open={showDisplaySettings}
                onCancel={() => setShowDisplaySettings(false)}
                onClose={() => setShowDisplaySettings(false)}
            />
            <ReservationPolicyUpdateConfirmationPopup
                open={showPolicyChangePopup}
                onApplyNewPolicy={handleApplyNewPolicy}
                onKeepCurrentPolicy={handleKeepCurrentPolicy}
                newPolicyDetails={policyDetails}
            />
        </React.Fragment>
    );
};

Scheduler.propTypes = {
    resourcesFunction: PropTypes.func.isRequired,
    eventsFunction: PropTypes.func.isRequired,
    eventClick: PropTypes.func,
    select: PropTypes.func,
    saveReservation: PropTypes.func,
    dayDataFunction: PropTypes.func,
    viewSettings: PropTypes.object,
    setViewSettings: PropTypes.func,
    priceDataFunction: PropTypes.func,
    hotelSettings: PropTypes.object,
    setHotelSettings: PropTypes.func,
    hotels: PropTypes.array,
    columnData: PropTypes.array,
    rebuildVirtualEvents: PropTypes.func,
    rebuildDayUseEvents: PropTypes.func,
    colDataFunction: PropTypes.func,
    HotelFilterCmp: PropTypes.func,
};

export default React.memo(Scheduler);